import { useCallback, useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import type { ZodTypeAny } from 'zod'
import Column from '@app/components/layout/Column'
import { Form } from '@app/components/core/form/Form'
import { TextField } from '@app/components/core/form/fields/TextField'
import { Toggle } from '@app/components/core/toggle/Toggle'
import { Alert } from '@app/components/core/alert/Alert'
import {
  ImageEditRow,
  type ExistingImage,
  type ImagePreview,
} from '@app/components/blocks/imageEditRow/ImageEditRow'
import {
  LocationMapPreview,
  type LocationGeocodeResult,
} from '@app/components/blocks/LocationMapPreview'
import { Text } from '@app/components/core/typography/Text'
import { CreationGuide } from '@app/pages/dashboard/initiative/create/blocks/CreationGuide'
import { useTranslations } from '@i18n/useLanguage'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { type LocationCoverFormValues } from '../shared/eventValidation'
import { eventCreationSteps, type EventCreationStep } from '../shared/workflow'
import { StepValuesSync } from '../shared/StepValuesSync'
import { Image } from '@app/components/core/image/Image'
import LocationPinIllustration from '@app/assets/images/illustrations/location-pin.png'
import type { LocationType } from '../shared/eventTypes'

const CURRENT_STEP: EventCreationStep = eventCreationSteps.locationCover

const EventLocationMapPreview = () => {
  const { setValue } = useFormContext<LocationCoverFormValues>()
  const address = useWatch<LocationCoverFormValues, 'address'>({
    name: 'address',
  })
  const handleGeocodeResult = useCallback(
    (result: LocationGeocodeResult | null) => {
      setValue(
        'geo',
        result ? { lat: result.lat, long: result.long } : undefined,
      )
    },
    [setValue],
  )

  return (
    <LocationMapPreview
      address={(address ?? '').trim()}
      onGeocodeResult={handleGeocodeResult}
    />
  )
}

type Props = {
  schema: ZodTypeAny
  locationType: LocationType
  setLocationType: (t: LocationType) => void
  defaultValues: LocationCoverFormValues
  eventId: number | null
  existingImages: ExistingImage[]
  onImagesChanged: () => void
  newImages: ImagePreview[]
  setNewImages: React.Dispatch<React.SetStateAction<ImagePreview[]>>
  removedImages: number[]
  setRemovedImages: React.Dispatch<React.SetStateAction<number[]>>
  onValuesChange: (values: LocationCoverFormValues) => void
}

export const LocationCoverStep = ({
  schema,
  locationType,
  setLocationType,
  defaultValues,
  eventId,
  existingImages,
  onImagesChanged,
  newImages,
  setNewImages,
  removedImages,
  setRemovedImages,
  onValuesChange,
}: Props) => {
  const t = useTranslations().app.dashboard.event.creation
  const client = useClient()
  const { showFailToast } = useToast()

  useEffect(() => {
    if (!eventId) {
      return
    }

    if (removedImages.length > 0) {
      Promise.all(
        removedImages.map((fileId) => client.call('delete-file', { fileId })),
      )
        .then(() => {
          setRemovedImages([])
          onImagesChanged()
        })
        .catch(() => showFailToast())
    }

    if (newImages.length > 0) {
      Promise.all(
        newImages.map((preview) =>
          client.stream('upload-file', {
            mimeType: preview.file.type,
            contents: preview.file,
            size: preview.file.size,
            fileName: `event-cover-${Date.now()}-${preview.file.name}`,
            payload: { eventImage: { id: eventId } },
          }),
        ),
      )
        .then(() => {
          newImages.forEach((preview) => URL.revokeObjectURL(preview.preview))
          setNewImages([])
          onImagesChanged()
        })
        .catch(() => showFailToast())
    }
  }, [
    client,
    eventId,
    newImages,
    removedImages,
    setNewImages,
    setRemovedImages,
    onImagesChanged,
    showFailToast,
  ])

  const stepTitles = { [CURRENT_STEP]: t.steps.locationCover.title }
  const stepDescriptions = {
    [CURRENT_STEP]: t.steps.locationCover.description,
  }

  const locationTypeOptions = [
    { label: t.offlineEvent, value: 'offline' as LocationType },
    { label: t.onlineEvent, value: 'online' as LocationType },
  ]

  return (
    <CreationGuide
      steps={[CURRENT_STEP]}
      currentStep={CURRENT_STEP}
      stepTitles={stepTitles}
      stepDescriptions={stepDescriptions}
      illustration={
        <Image
          src={LocationPinIllustration}
          alt="Location Pin Illustration"
          style={{ maxWidth: '15rem' }}
        />
      }
    >
      <Form<LocationCoverFormValues>
        key={locationType}
        schema={schema}
        defaultValues={defaultValues}
        mode="onChange"
        onSubmit={() => undefined}
      >
        <Column gap="2rem" alignItems="stretch" style={{ marginTop: '1.5rem' }}>
          <Toggle
            options={locationTypeOptions}
            selectedOption={locationType}
            setSelectedOption={setLocationType}
            style={{ alignSelf: 'stretch' }}
          />
          <StepValuesSync<LocationCoverFormValues>
            onValuesChange={onValuesChange}
          />
          {locationType === 'offline' ? (
            <>
              <TextField<LocationCoverFormValues>
                name="address"
                label={t.locationLabel}
                placeholder={t.locationPlaceholder}
              />
              <EventLocationMapPreview />
            </>
          ) : (
            <TextField<LocationCoverFormValues>
              name="meetingLink"
              label={t.meetingLinkLabel}
              placeholder={t.meetingLinkPlaceholder}
              tooltipText={t.meetingLinkTooltip}
            />
          )}
          <Column gap="0.5rem" alignItems="stretch">
            <Text.Medium style={{ fontWeight: 500 }}>
              {t.coverImageLabel}
            </Text.Medium>
            <ImageEditRow
              existingImages={existingImages}
              newImages={newImages}
              removedImages={removedImages}
              setNewImages={setNewImages}
              setRemovedImages={setRemovedImages}
              maxImages={1}
            />
            <Alert variant="info" size="medium" message={t.coverImageInfo} />
          </Column>
        </Column>
      </Form>
    </CreationGuide>
  )
}
